import type { LucideIcon } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@wa/ui";
import type { TabKey } from "./tabs";

/**
 * One menu tab, drawn in the side menu and in the narrow-screen hamburger.
 *
 * `pill` is the one thing a caller has to think about. The active marker is a
 * shared `layoutId`, which is what makes it slide from tab to tab - and a
 * `layoutId` may only be alive in ONE place at a time. The side menu keeps its
 * account tabs mounted under `lg` (hidden by CSS, not unmounted), so the copies
 * in the hamburger must opt out or the marker jumps between the two.
 */
export function TabButton({
  tab, Icon, active, label, onClick, className, pill = true,
}: {
  tab: TabKey;
  Icon: LucideIcon;
  active: boolean;
  label: string;
  onClick: () => void;
  className?: string;
  pill?: boolean;
}) {
  return (
    <button
      key={tab}
      aria-current={active ? "page" : undefined}
      onClick={onClick}
      className={cn(
        "relative flex shrink-0 items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors",
        active ? "text-foreground" : "text-muted-foreground hover:bg-accent/50 hover:text-foreground",
        className,
      )}
    >
      {active && (pill ? (
        <motion.span
          layoutId="account-tab-pill"
          className="absolute inset-0 rounded-lg bg-accent"
          transition={{ type: "spring", stiffness: 400, damping: 32 }}
        />
      ) : (
        <span className="absolute inset-0 rounded-lg bg-accent" />
      ))}
      <Icon className="relative size-4" />
      <span className="relative">{label}</span>
    </button>
  );
}
